// ═══════════════════════════════════════════════════════════════
// PACE — Plans
// Create, view, and acknowledge org-wide PACE plans
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import {
  Title, Text, Group, Box, Button, Paper, ActionIcon, Badge, Loader,
  Center, Stack, Modal, TextInput, Textarea, Select, SimpleGrid, Collapse,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import { IconPlus, IconTrash, IconChevronDown, IconChevronUp,
  IconCheck, IconFileText, IconBell } from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { usePaceStore, PACE_TIERS, PACE_TIER_LABELS, COMM_METHOD_LABELS } from '../../store/paceStore';
import type { PaceTier, CommMethod, PacePlan } from '../../store/paceStore';

const TIER_BG: Record<PaceTier, { bg: string; border: string }> = {
  primary: { bg: 'var(--mantine-color-green-0)', border: 'var(--mantine-color-green-3)' },
  alternate: { bg: 'var(--mantine-color-blue-0)', border: 'var(--mantine-color-blue-3)' },
  contingency: { bg: 'var(--mantine-color-yellow-0)', border: 'var(--mantine-color-yellow-3)' },
  emergency: { bg: 'var(--mantine-color-red-0)', border: 'var(--mantine-color-red-3)' },
};

const emptyEntry = (tier: PaceTier, order: number) => ({
  tier, method: 'cell_phone' as CommMethod, contactValue: '', notes: '', order,
});

export default function Plans() {
  const { user } = useAuthStore();
  const {
    plans, loading, loadPlans,
    createPlan, createPlanEntry, deletePlan, updatePlan,
  } = usePaceStore();

  const [showNew, setShowNew] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState<string | null>(null);
  const [newPlan, setNewPlan] = useState({ name: '', description: '' });
  const [entries, setEntries] = useState(PACE_TIERS.map((t, i) => emptyEntry(t, i)));
  const [creating, setCreating] = useState(false);
  const [acknowledging, setAcknowledging] = useState<number | null>(null);

  useEffect(() => { loadPlans(); }, []);

  // Split plans into user's own and others'
  const myEmail = user?.email ?? '';
  const myFullName = user?.fullName ?? '';
  const myPlans = plans.filter(p => p.creator === myFullName || p.creator === myEmail);
  const orgPlans = plans.filter(p => p.creator !== myFullName && p.creator !== myEmail);
  const unackedOrgPlans = orgPlans.filter(p => {
    const acked = p.acknowledgedBy?.split(',').map(s => s.trim()).filter(Boolean) ?? [];
    return !acked.includes(myEmail);
  });

  const handleCreate = async () => {
    if (!newPlan.name.trim()) return;
    setCreating(true);
    try {
      const planId = await createPlan({
        planName: newPlan.name.trim(),
        description: newPlan.description.trim(),
      });
      // Create entries
      for (const entry of entries.filter(e => e.contactValue.trim())) {
        await createPlanEntry({
          planId: String(planId),
          tier: entry.tier,
          method: entry.method,
          contactValue: entry.contactValue,
          notes: entry.notes,
          sortOrder: String(entry.order),
        });
      }
      notifications.show({ message: 'Plan created!', color: 'green' });
      setShowNew(false);
      setNewPlan({ name: '', description: '' });
      setEntries(PACE_TIERS.map((t, i) => emptyEntry(t, i)));
    } catch { notifications.show({ message: 'Failed to create plan', color: 'red' }); }
    finally { setCreating(false); }
  };

  const handleDelete = async (planId: number, planName: string) => {
    if (!confirm(`Delete plan "${planName}"?`)) return;
    try {
      await deletePlan(planId);
      notifications.show({ message: 'Plan deleted', color: 'green' });
    } catch { notifications.show({ message: 'Failed to delete plan', color: 'red' }); }
  };

  const handleAcknowledge = async (plan: PacePlan) => {
    if (!user) return;
    const existing = plan.acknowledgedBy ? plan.acknowledgedBy.split(',').map(s => s.trim()).filter(Boolean) : [];
    if (existing.includes(user.email ?? '')) return;
    setAcknowledging(plan.id);
    try {
      const updated = [...existing, user.email ?? ''].join(', ');
      await updatePlan(plan.id, { acknowledgedBy: updated });
      notifications.show({ message: 'Plan acknowledged!', color: 'green' });
    } catch { notifications.show({ message: 'Failed to acknowledge', color: 'red' }); }
    finally { setAcknowledging(null); }
  };

  const updateEntry = (idx: number, field: string, value: string) => {
    setEntries(es => es.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  };

  const methodOptions = Object.entries(COMM_METHOD_LABELS).map(([v, l]) => ({ value: v, label: l }));

  const renderPlanCard = (plan: PacePlan, canDelete: boolean) => {
    const acked = plan.acknowledgedBy?.split(',').map(s => s.trim()).filter(Boolean) ?? [];
    const myAcked = acked.includes(myEmail);
    const isExpanded = expandedPlan === String(plan.id);
    return (
      <Paper key={plan.id} withBorder style={{ overflow: 'hidden' }}>
        <Box px="md" py="sm" style={{ cursor: 'pointer' }}
          onClick={() => setExpandedPlan(isExpanded ? null : String(plan.id))}>
          <Group justify="space-between" wrap="nowrap">
            <Box style={{ flex: 1 }}>
              <Group gap="xs">
                <Text fw={600}>{plan.planName}</Text>
                <Badge color="blue" variant="light" size="sm">Org-wide</Badge>
                {myAcked && (
                  <Badge color="green" size="xs" variant="light" leftSection={<IconCheck size={10} />}>
                    Acknowledged
                  </Badge>
                )}
                {!myAcked && !canDelete && (
                  <Badge color="orange" size="xs" variant="light" leftSection={<IconBell size={10} />}>
                    Needs Acknowledgment
                  </Badge>
                )}
              </Group>
              {plan.description && <Text size="sm" c="dimmed" mt={2}>{plan.description}</Text>}
              <Text size="xs" c="dimmed" mt={4}>
                {acked.length} acknowledgment{acked.length !== 1 ? 's' : ''}
              </Text>
            </Box>
            <Group gap="xs">
              {canDelete && (
                <ActionIcon variant="subtle" color="red"
                  onClick={e => { e.stopPropagation(); handleDelete(plan.id, plan.planName); }}>
                  <IconTrash size={14} />
                </ActionIcon>
              )}
              {isExpanded ? <IconChevronUp size={14} color="#9ca3af" /> : <IconChevronDown size={14} color="#9ca3af" />}
            </Group>
          </Group>
        </Box>

        <Collapse in={isExpanded}>
          <PlanDetail plan={plan} />
          <Box px="md" pb="md">
            {myAcked ? (
              <Group gap="xs">
                <IconCheck size={14} color="#16a34a" />
                <Text size="sm" fw={500} c="green.7">You acknowledged this plan</Text>
              </Group>
            ) : (
              <Button onClick={() => handleAcknowledge(plan)} loading={acknowledging === plan.id}
                leftSection={<IconCheck size={14} />}>
                Acknowledge Plan
              </Button>
            )}
          </Box>
        </Collapse>
      </Paper>
    );
  };

  return (
    <Box>
      <Group justify="space-between" mb="lg">
        <Box>
          <Title order={3} c="black">PACE Plans</Title>
          <Text c="dimmed" mt={4}>Org-wide communication plans shared with your team.</Text>
        </Box>
        <Button leftSection={<IconPlus size={14} />} onClick={() => setShowNew(true)}>New Plan</Button>
      </Group>

      {loading ? (
        <Center py="xl" mih={200}><Loader /></Center>
      ) : plans.length === 0 ? (
        <Center py="xl" mih={200}>
          <Stack align="center">
            <IconFileText size={40} color="#d1d5db" style={{ opacity: 0.3 }} />
            <Text fw={500}>No plans yet</Text>
            <Text size="sm" c="dimmed">Create your first org-wide PACE plan, or wait for your team to share one.</Text>
            <Button onClick={() => setShowNew(true)}>Create Plan</Button>
          </Stack>
        </Center>
      ) : (
        <Stack gap="lg">
          {/* Unacknowledged plans from others — show prominently */}
          {unackedOrgPlans.length > 0 && (
            <Paper p="md" withBorder style={{ borderColor: '#fde68a', background: '#fffbeb' }}>
              <Group gap="xs" mb="xs">
                <IconBell size={16} color="#a16207" />
                <Text fw={600} size="sm" style={{ color: '#92400e' }}>
                  {unackedOrgPlans.length} plan{unackedOrgPlans.length !== 1 ? 's' : ''} need{unackedOrgPlans.length === 1 ? 's' : ''} your acknowledgment
                </Text>
              </Group>
              <Text size="xs" style={{ color: '#a16207' }}>
                Expand the plans below and click "Acknowledge Plan" to confirm you've reviewed them.
              </Text>
            </Paper>
          )}

          {/* Organization Plans (from others) */}
          {orgPlans.length > 0 && (
            <Box>
              <Text fw={600} size="sm" c="dimmed" tt="uppercase" mb="xs">
                Organization Plans ({orgPlans.length})
              </Text>
              <Stack gap="sm">
                {orgPlans.map(plan => renderPlanCard(plan, false))}
              </Stack>
            </Box>
          )}

          {/* My Plans */}
          {myPlans.length > 0 && (
            <Box>
              <Text fw={600} size="sm" c="dimmed" tt="uppercase" mb="xs">
                Your Plans ({myPlans.length})
              </Text>
              <Stack gap="sm">
                {myPlans.map(plan => renderPlanCard(plan, true))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}

      {/* New Plan Modal */}
      <Modal opened={showNew} onClose={() => setShowNew(false)} title="Create PACE Plan" size="lg">
        <Stack gap="md">
          <TextInput label="Plan Name *" value={newPlan.name}
            onChange={e => setNewPlan(p => ({ ...p, name: e.target.value }))}
            placeholder="e.g. Earthquake Response Plan" />
          <Textarea label="Description" value={newPlan.description}
            onChange={e => setNewPlan(p => ({ ...p, description: e.target.value }))}
            rows={2} placeholder="Briefly describe when this plan is used..." />

          <Box>
            <Text size="sm" fw={500} mb="sm">Communication Tiers</Text>
            <Stack gap="sm">
              {PACE_TIERS.map((tier, idx) => {
                const tierStyle = TIER_BG[tier] ?? { bg: 'var(--mantine-color-gray-0)', border: 'var(--mantine-color-gray-3)' };
                return (
                <Paper key={tier} p="md" style={{ background: tierStyle.bg, border: `1px solid ${tierStyle.border}` }}>
                  <Text size="xs" fw={700} tt="uppercase" mb="sm">{PACE_TIER_LABELS[tier]}</Text>
                  <SimpleGrid cols={2}>
                    <Select label="Method" size="sm" value={entries[idx].method}
                      onChange={(val) => updateEntry(idx, 'method', val || 'cell_phone')} data={methodOptions} />
                    <TextInput label="Contact / Value *" size="sm" value={entries[idx].contactValue}
                      onChange={e => updateEntry(idx, 'contactValue', e.target.value)}
                      placeholder="Phone, frequency, location..." />
                  </SimpleGrid>
                  <TextInput label="Notes" size="sm" mt="xs" value={entries[idx].notes}
                    onChange={e => updateEntry(idx, 'notes', e.target.value)}
                    placeholder="Special instructions, when to use..." />
                </Paper>
              );
              })}
            </Stack>
          </Box>
        </Stack>

        <Group mt="lg">
          <Button flex={1} onClick={handleCreate} disabled={creating || !newPlan.name.trim()} loading={creating}>
            Create Plan
          </Button>
          <Button variant="default" onClick={() => setShowNew(false)}>Cancel</Button>
        </Group>
      </Modal>
    </Box>
  );
}

function PlanDetail({ plan }: { plan: PacePlan }) {
  // Parse entries from the plan's entriesJson field
  // Each entry: { tier, method, contactValue, notes, order }
  let entries: Array<{ tier: string; method: string; contactValue: string; notes: string; order: string | number }> = [];
  try { entries = JSON.parse(plan.entriesJson || '[]'); } catch { entries = []; }

  const TIER_BG_COLORS: Record<string, { bg: string; border: string; color: string }> = {
    primary:     { bg: '#f0fdf4', border: '#bbf7d0', color: '#15803d' },
    alternate:   { bg: '#eff6ff', border: '#bfdbfe', color: '#1d4ed8' },
    contingency: { bg: '#fefce8', border: '#fef08a', color: '#a16207' },
    emergency:   { bg: '#fef2f2', border: '#fecaca', color: '#b91c1c' },
  };

  if (entries.length === 0) {
    return (
      <Box px="md" py="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
        <Text size="sm" c="dimmed" fs="italic">No communication tiers configured for this plan.</Text>
      </Box>
    );
  }

  return (
    <Box px="md" py="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        {PACE_TIERS.map(tier => {
          const entry = entries.find(e => e.tier === tier);
          const colors = TIER_BG_COLORS[tier] ?? TIER_BG_COLORS.primary;
          if (!entry) return (
            <Paper key={tier} p="sm" style={{ background: '#f9fafb', border: '1px solid #e5e7eb', opacity: 0.6 }}>
              <Text size="xs" fw={700} tt="uppercase" mb={4} style={{ color: '#9ca3af' }}>
                {PACE_TIER_LABELS[tier]}
              </Text>
              <Text size="xs" c="dimmed" fs="italic">Not configured</Text>
            </Paper>
          );
          return (
            <Paper key={tier} p="sm" style={{ background: colors.bg, border: `1px solid ${colors.border}` }}>
              <Text size="xs" fw={700} tt="uppercase" mb={4} style={{ color: colors.color }}>
                {PACE_TIER_LABELS[tier]}
              </Text>
              <Text size="xs" fw={500}>{COMM_METHOD_LABELS[entry.method as CommMethod] || entry.method}</Text>
              {entry.contactValue && (
                <Text size="xs" mt={4} ff="monospace" style={{ wordBreak: 'break-word' }}>{entry.contactValue}</Text>
              )}
              {entry.notes && (
                <Text size="xs" c="dimmed" mt={4} fs="italic">{entry.notes}</Text>
              )}
            </Paper>
          );
        })}
      </SimpleGrid>
    </Box>
  );
}
